import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBriefcase, FiEdit2, FiEye, FiKey, FiLink, FiTrash2 } from 'react-icons/fi';
import Layout from '../../components/Layout';
import api from '../../api';

export default function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [filter, setFilter] = useState('');
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.assign('/login');
      return;
    }

    api
      .get('/admin/users', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setUsers(res.data.users || []))
      .catch((err) => setError(err.response?.data?.message || 'Could not load users'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) => {
      const haystack = [
        `${user.firstName || ''} ${user.lastName || ''}`,
        user.email,
        user.phone,
        user.shimbirrId,
        user.businessSummary?.businessName,
        user.businessSummary?.businessStatus,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [filter, users]);

  const suiteReady = users.filter((user) => user.businessSummary?.businessStatus === 'suite_ready').length;
  const connected = users.filter((user) => (user.businessSummary?.totalConnectedPortals || 0) > 0).length;

  const confirmDelete = async (id) => {
    const token = localStorage.getItem('token');
    await api.delete(`/admin/users/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setUsers((current) => current.filter((entry) => entry._id !== id));
    setDeleteUser(null);
  };

  const handleSave = async (updated) => {
    const token = localStorage.getItem('token');
    const res = await api.patch(`/admin/users/${updated._id}`, updated, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const fresh = res.data.user;
    setUsers((current) =>
      current.map((entry) =>
        entry._id === fresh._id ? { ...entry, ...fresh } : entry
      )
    );
    setEditUser(null);
  };

  const sendResetPassword = async (user) => {
    const token = localStorage.getItem('token');
    const res = await api.post(
      `/admin/users/${user._id}/reset-password`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setNotice({
      tone: 'success',
      message: res.data?.message
        ? `${res.data.message} to ${user.email}.`
        : `Password reset code sent to ${user.email}.`,
    });
    setResetUser(null);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <p className="text-white">Loading users...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <p className="text-red-400">{error}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 p-6">
        <section className="rounded-[2rem] border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top,rgba(245,178,33,0.18),transparent_28%),linear-gradient(180deg,#171717,#090909)] p-8 shadow-2xl">
          <p className="text-sm uppercase tracking-[0.24em] text-[#f7d88a]">User Management</p>
          <h1 className="mt-4 text-4xl font-black tracking-[-0.03em] text-white">
            Support customers across identity, business profile, and software suite access.
          </h1>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <Metric label="Total users" value={String(users.length)} />
            <Metric label="Suite ready" value={String(suiteReady)} />
            <Metric label="Connected portals" value={String(connected)} />
          </div>
        </section>

        <section className="rounded-[2rem] border border-[#f5b221]/12 bg-[linear-gradient(180deg,#141414,#090909)] p-6 shadow-xl">
          {notice ? (
            <div
              className={`mb-5 rounded-[1.5rem] border px-4 py-3 text-sm ${
                notice.tone === 'success'
                  ? 'border-[#f5b221]/25 bg-[#f5b221]/10 text-[#f7d88a]'
                  : 'border-amber-500/25 bg-amber-500/10 text-amber-100'
              }`}
            >
              {notice.message}
            </div>
          ) : null}

          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">Customer list</h2>
              <p className="mt-2 text-sm text-slate-400">
                Search by person, Shimbirr ID, or business profile and jump straight into support actions.
              </p>
            </div>
            <input
              type="text"
              placeholder="Search name, email, Shimbirr ID, business..."
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              className="w-full rounded-2xl border border-gray-700 bg-black px-4 py-3 text-white outline-none transition focus:border-amber-300 lg:max-w-md"
            />
          </div>

          <div className="mt-6 overflow-hidden rounded-3xl border border-white/5">
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate text-sm [border-spacing:0]">
                <thead className="bg-[#f2c14b] text-left text-[11px] uppercase tracking-[0.2em] text-black">
                  <tr>
                    <Th first>User</Th>
                    <Th>Business</Th>
                    <Th>Suite status</Th>
                    <Th>Portals</Th>
                    <Th>Joined</Th>
                    <Th className="text-center">Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((user) => (
                    <tr key={user._id} className="border-t border-white/5 bg-[#151515] align-top">
                      <Td first>
                        <p className="font-semibold text-white">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">{user.email}</p>
                        <p className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-500">
                          {user.shimbirrId || 'Pending ID'}
                        </p>
                      </Td>
                      <Td>
                        <div className="inline-flex items-start gap-2">
                          <FiBriefcase className="mt-0.5 text-amber-300" />
                          <div>
                            <p className="font-medium text-white">
                              {user.businessSummary?.businessName || 'No business profile yet'}
                            </p>
                            <p className="mt-1 text-xs text-slate-400">{user.phone || 'No phone saved'}</p>
                          </div>
                        </div>
                      </Td>
                      <Td>
                        <div className="space-y-2">
                          <BusinessStatusPill status={user.businessSummary?.businessStatus} />
                          <p className="text-xs text-slate-400">
                            {(user.businessSummary?.readinessScore ?? 0)}% ready · {user.businessSummary?.issueCount || 0} issue{user.businessSummary?.issueCount === 1 ? '' : 's'}
                          </p>
                        </div>
                      </Td>
                      <Td>
                        <div className="inline-flex items-start gap-2">
                          <FiLink className="mt-0.5 text-amber-200" />
                          <div>
                            <p className="font-medium text-white">
                              {user.businessSummary?.totalConnectedPortals || 0} portal{user.businessSummary?.totalConnectedPortals === 1 ? '' : 's'}
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                              {user.businessSummary?.lastSyncedFrom || 'No suite sync source'}
                            </p>
                          </div>
                        </div>
                      </Td>
                      <Td>
                        <span className="text-slate-300">
                          {new Date(user.createdAt).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </Td>
                      <Td className="text-center">
                        <button
                          onClick={() => navigate(`/users/${user._id}`, { state: { user } })}
                          className="inline-flex rounded p-2 hover:bg-[#f5b221]/12"
                          title="View"
                          aria-label={`View ${user.firstName} ${user.lastName}`}
                        >
                          <FiEye className="h-4 w-4 text-[#f7d88a]" />
                        </button>
                        <button
                          onClick={() => setResetUser(user)}
                          className="inline-flex rounded p-2 hover:bg-[#f5b221]/20"
                          title="Send reset code"
                          aria-label={`Send password reset code to ${user.firstName} ${user.lastName}`}
                        >
                          <FiKey className="h-4 w-4 text-[#f5b221]" />
                        </button>
                        <button
                          onClick={() => setEditUser(user)}
                          className="inline-flex rounded p-2 hover:bg-amber-400/20"
                          title="Edit"
                        >
                          <FiEdit2 className="h-4 w-4 text-amber-300" />
                        </button>
                        <button
                          onClick={() => setDeleteUser(user)}
                          className="inline-flex rounded p-2 hover:bg-red-600/20"
                          title="Delete"
                        >
                          <FiTrash2 className="h-4 w-4 text-red-400" />
                        </button>
                      </Td>
                    </tr>
                  ))}
                  {!filtered.length ? (
                    <tr className="bg-[#151515]">
                      <td colSpan={6} className="px-6 py-10 text-center text-sm text-slate-400">
                        No users matched this search.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {editUser ? (
          <EditModal
            user={editUser}
            onSave={handleSave}
            onCancel={() => setEditUser(null)}
          />
        ) : null}

        {deleteUser ? (
          <DeleteModal
            user={deleteUser}
            onConfirm={() => confirmDelete(deleteUser._id)}
            onCancel={() => setDeleteUser(null)}
          />
        ) : null}

        {resetUser ? (
          <ResetPasswordModal
            user={resetUser}
            onConfirm={() => sendResetPassword(resetUser)}
            onCancel={() => setResetUser(null)}
          />
        ) : null}
      </div>
    </Layout>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-3xl border border-white/5 bg-black/30 p-5">
      <p className="text-xs uppercase tracking-[0.22em] text-slate-400">{label}</p>
      <p className="mt-2 text-3xl font-black tracking-[-0.03em] text-white">{value}</p>
    </div>
  );
}

function BusinessStatusPill({ status }) {
  const tone = {
    suite_ready: 'border-yellow-400/30 bg-yellow-400/12 text-yellow-100',
    needs_attention: 'border-amber-400/30 bg-amber-500/10 text-amber-100',
    attention: 'border-rose-400/30 bg-rose-500/10 text-rose-100',
    personal_only: 'border-slate-400/20 bg-slate-500/10 text-slate-100',
  }[String(status || '').toLowerCase()] || 'border-slate-400/20 bg-slate-500/10 text-slate-100';

  return (
    <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] ${tone}`}>
      {String(status || 'unknown').replace(/_/g, ' ')}
    </span>
  );
}

function Th({ children, first = false, className = '' }) {
  return (
    <th
      scope="col"
      className={`px-5 py-4 font-semibold ${first ? 'rounded-tl-3xl ' : ''}${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, first = false, className = '' }) {
  return (
    <td className={`px-5 py-4 text-gray-200 ${first ? 'min-w-[16rem] ' : ''}${className}`}>
      {children}
    </td>
  );
}

function EditModal({ user, onSave, onCancel }) {
  const [form, setForm] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
    phone: user.phone || '',
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave({ ...user, ...form });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-3xl border border-[#f5b221]/18 bg-[linear-gradient(180deg,#171717,#090909)] p-6 text-white shadow-2xl"
      >
        <h3 className="text-xl font-semibold">Edit user</h3>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="text-sm text-slate-300">First name</span>
            <input
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              className="mt-2 w-full rounded-2xl border border-[#f5b221]/16 bg-black/60 px-4 py-3"
            />
          </label>
          <label className="block">
            <span className="text-sm text-slate-300">Last name</span>
            <input
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              className="mt-2 w-full rounded-2xl border border-[#f5b221]/16 bg-black/60 px-4 py-3"
            />
          </label>
          <label className="block">
            <span className="text-sm text-slate-300">Phone</span>
            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              className="mt-2 w-full rounded-2xl border border-[#f5b221]/16 bg-black/60 px-4 py-3"
            />
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 hover:bg-white/10">
            Cancel
          </button>
          <button type="submit" className="rounded-full bg-[#f5b221] px-4 py-2 font-semibold text-black hover:bg-[#f7d88a]">
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

function DeleteModal({ user, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-3xl border border-[#f5b221]/18 bg-[linear-gradient(180deg,#171717,#090909)] p-6 text-white shadow-2xl">
        <h3 className="text-xl font-semibold text-[#f7d88a]">Delete user?</h3>
        <p className="mt-3 text-sm leading-6 text-slate-300">
          This will remove <span className="font-semibold text-white">{user.firstName} {user.lastName}</span> from Shimbirr admin records.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 hover:bg-white/10">
            Cancel
          </button>
          <button onClick={onConfirm} className="rounded-full bg-[#f5b221] px-4 py-2 font-semibold text-black hover:bg-[#f7d88a]">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onConfirm, onCancel }) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    try {
      setSending(true);
      setError('');
      await onConfirm();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not send reset code');
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-3xl border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top,rgba(245,178,33,0.16),transparent_42%),linear-gradient(180deg,#171717,#090909)] p-6 text-white shadow-2xl">
        <h3 className="text-xl font-semibold text-[#f7d88a]">Send password reset?</h3>
        <p className="mt-3 text-sm leading-6 text-slate-300">
          Shimbirr will email a password reset code to <span className="font-semibold text-white">{user.email}</span> for{' '}
          <span className="font-semibold text-white">{user.firstName} {user.lastName}</span>.
        </p>
        {error ? (
          <div className="mt-4 rounded-2xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
            {error}
          </div>
        ) : null}
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={sending}
            className="rounded-full border border-white/10 bg-white/5 px-4 py-2 hover:bg-white/10 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={sending}
            className="rounded-full bg-[#f5b221] px-4 py-2 font-semibold text-black hover:bg-[#f7d88a] disabled:opacity-60"
          >
            {sending ? 'Sending…' : 'Send reset code'}
          </button>
        </div>
      </div>
    </div>
  );
}
